import { useCallback, useEffect, useRef, useState } from "react";
import { useAccount, useToast } from "../context";
import {
  ContactResponse,
  ConversationData,
  MessageStatus,
  MessageType,
} from "../interfaces/all";
import { apiService } from "../services/axios";

interface UseConversationsManagerProps {
  onConversationUpdate?: (conversations: ConversationData[]) => void;
  onNewMessage?: (phoneNumber: string, message: MessageType) => void;
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseMultipleCampaignsProps {
  campaigns: { id: string | number; name: string }[];
  onConversationUpdate?: (conversations: ConversationData[]) => void;
  onNewMessage?: (phoneNumber: string, message: MessageType) => void;
  eagerLoad?: boolean; // quando false, só carrega campanhas ao expandir
}

interface ConversationUpdate {
  phoneNumber: string;
  message: MessageType;
  timestamp: number;
}

// Extensão da interface ContactResponse para incluir hasMore
interface ContactResponseWithHasMore extends ContactResponse {
  hasMore?: boolean;
}

export const useConversationsManager = ({
  onConversationUpdate,
  onNewMessage,
  autoRefresh = false,
  refreshInterval = 60000, // 60 segundos
}: UseConversationsManagerProps = {}) => {
  const [conversations, setConversations] = useState<ConversationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { account, socket } = useAccount();
  const { showError } = useToast();

  // Refs para otimização
  const conversationsRef = useRef<ConversationData[]>([]);
  const lastUpdateRef = useRef<Map<string, number>>(new Map());
  const updateQueueRef = useRef<ConversationUpdate[]>([]);
  const processingRef = useRef(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const batchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const lastConversationsHashRef = useRef<string>("");
  const loadingRef = useRef(false);

  // Função para normalizar números de telefone
  const normalizePhoneNumber = useCallback((phone: string): string => {
    if (!phone) return "";
    return phone.replace(/[^0-9]/g, "").replace(/^55/, "");
  }, []);

  // Função para gerar hash das conversas para comparação
  const generateConversationsHash = useCallback(
    (conversations: ConversationData[]) => {
      return conversations
        .map((conv) => `${conv.id}-${conv.last_message?.timestamp || 0}`)
        .join("|");
    },
    [],
  );

  // Função para carregar conversas da API (otimizada para evitar callbacks múltiplos)
  const loadConversations = useCallback(
    async (forceRefresh = false, skipCallback = false) => {
      if (!account?.id || !isMountedRef.current) return;

      if (loadingRef.current && !forceRefresh) return;

      setLoading(true);
      loadingRef.current = true;
      setError(null);

      try {
        const response = await apiService.get<ContactResponse>("/contacts", {
          params: { email: account.email },
        });

        if (response.status === "success") {
          const responseData = response.data as ContactResponse;

          if (Array.isArray(responseData.contacts)) {
            const conversationsData = responseData.contacts.map(
              (contact): ConversationData => ({
                id: contact.id.toString(),
                name: contact.name,
                email: contact.email,
                telephone: contact.telephone || contact.phone || "",
                valid_name: contact.valid_name || contact.name,
                valid_telephone:
                  contact.valid_telephone ||
                  contact.telephone ||
                  contact.phone ||
                  "",
                status: contact.status || "ATIVO",
                campaign_name: contact.campaign_name,
                campaign_id: contact.campaign_id.toString(),
                attendant_id: contact.attendant_id
                  ? contact.attendant_id.toString()
                  : "",
                attendant_name: contact.attendant_name,
                coord_id: contact.coord_id ? contact.coord_id.toString() : "",
                coordinator_name: contact.coordinator_name,
                company_id: contact.company_id.toString(),
                last_message: contact.last_message,
                is_online: false,
                ai_state: contact.ai_state
                  ? {
                      isAIActive: contact.ai_state.isAIActive,
                      disabledReason: contact.ai_state.disabledReason,
                      updatedAt: new Date(contact.ai_state.updatedAt),
                    }
                  : {
                      isAIActive: true,
                      disabledReason: undefined,
                      updatedAt: new Date(),
                    },
              }),
            );

            // Verificar se realmente houve mudanças
            const newHash = generateConversationsHash(conversationsData);
            if (newHash === lastConversationsHashRef.current && !forceRefresh) {
              setLoading(false);
              loadingRef.current = false;
              return;
            }

            lastConversationsHashRef.current = newHash;
            setConversations(conversationsData);
            conversationsRef.current = conversationsData;

            // Inicializar mapa de últimas atualizações
            conversationsData.forEach((conv) => {
              if (conv.last_message?.timestamp) {
                const phoneNumber = conv.valid_telephone || conv.telephone;
                lastUpdateRef.current.set(
                  normalizePhoneNumber(phoneNumber),
                  new Date(conv.last_message.timestamp).getTime(),
                );
              }
            });

            // Callback apenas se não for skipCallback (para evitar callbacks duplos no auto-refresh)
            if (!skipCallback && onConversationUpdate) {
              onConversationUpdate(conversationsData);
            }
          } else {
            throw new Error("Formato de resposta inválido");
          }
        } else {
          throw new Error("Erro na resposta da API");
        }
      } catch (error) {
        console.error("Erro ao carregar conversas:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Erro desconhecido";
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [account?.cpf, showError, onConversationUpdate, generateConversationsHash],
  );

  // Função otimizada para atualizar uma conversa
  const updateConversation = useCallback(
    (phoneNumber: string, newMessage: MessageType) => {
      if (!isMountedRef.current) return;

      const normalizedInputPhone = normalizePhoneNumber(phoneNumber);
      const messageTime = new Date(newMessage.timestamp).getTime();
      const lastUpdate = lastUpdateRef.current.get(normalizedInputPhone) || 0;

      // Só atualizar se a mensagem for mais recente
      if (messageTime > lastUpdate) {
        lastUpdateRef.current.set(normalizedInputPhone, messageTime);

        setConversations((prevConversations) => {
          const updatedConversations = prevConversations.map((conv) => {
            const convPhone = conv.valid_telephone || conv.telephone;
            if (normalizePhoneNumber(convPhone) === normalizedInputPhone) {
              // Só atualizar se a mensagem for realmente diferente
              const currentLastMessage = conv.last_message;
              if (currentLastMessage?._id !== newMessage._id) {
                return {
                  ...conv,
                  last_message: {
                    ...newMessage,
                  },
                };
              }
              return conv;
            }
            return conv;
          });

          conversationsRef.current = updatedConversations;

          // Callback direto
          if (onConversationUpdate) {
            onConversationUpdate(updatedConversations);
          }

          return updatedConversations;
        });

        if (onNewMessage) {
          onNewMessage(phoneNumber, newMessage);
        }
      }
    },
    [onConversationUpdate, onNewMessage],
  );

  // Função para agendar refresh
  const scheduleRefresh = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    refreshTimeoutRef.current = setTimeout(async () => {
      if (isMountedRef.current && !loadingRef.current) {
        try {
          // NÃO setar loading para auto-refresh (evita indicador de carregamento)
          const response = await apiService.get<ContactResponse>("/contacts", {
            params: { email: account?.email },
          });

          if (response.status === "success" && isMountedRef.current) {
            const responseData = response.data as ContactResponse;

            console.log(
              "🐛 [DEBUG FRONTEND] loadConversations (/contacts) fetched:",
              responseData.contacts?.length,
              "contacts",
            );
            if (Array.isArray(responseData.contacts)) {
              responseData.contacts.forEach((c) => {
                console.log(
                  `🐛 [DEBUG FRONTEND] Contact API: id=${c.id}, phone=${c.telephone || c.phone}, valid_telephone=${c.valid_telephone}`,
                );
              });

              const conversationsData = responseData.contacts.map(
                (contact): ConversationData => ({
                  id: contact.id.toString(),
                  name: contact.name,
                  email: contact.email,
                  telephone: contact.telephone || contact.phone || "",
                  valid_name: contact.valid_name || contact.name,
                  valid_telephone:
                    contact.valid_telephone ||
                    contact.telephone ||
                    contact.phone ||
                    "",
                  status: contact.status || "ATIVO",
                  campaign_name: contact.campaign_name,
                  campaign_id: contact.campaign_id.toString(),
                  attendant_id: contact.attendant_id
                    ? contact.attendant_id.toString()
                    : "",
                  attendant_name: contact.attendant_name,
                  coord_id: contact.coord_id ? contact.coord_id.toString() : "",
                  coordinator_name: contact.coordinator_name,
                  company_id: contact.company_id.toString(),
                  last_message: contact.last_message,
                  is_online: false,
                  ai_state: contact.ai_state
                    ? {
                        isAIActive: contact.ai_state.isAIActive,
                        disabledReason: contact.ai_state.disabledReason,
                        updatedAt: new Date(contact.ai_state.updatedAt),
                      }
                    : {
                        isAIActive: true,
                        disabledReason: undefined,
                        updatedAt: new Date(),
                      },
                }),
              );

              // Verificar se realmente houve mudanças
              const newHash = generateConversationsHash(conversationsData);

              if (newHash !== lastConversationsHashRef.current) {
                lastConversationsHashRef.current = newHash;

                // Atualização silenciosa sem loading state
                setConversations(conversationsData);
                conversationsRef.current = conversationsData;

                // Callback silencioso para auto-refresh (apenas se houver mudanças reais)
                if (onConversationUpdate) {
                  // Usar setTimeout para garantir que seja assíncrono e não cause flickering
                  setTimeout(() => {
                    if (isMountedRef.current) {
                      onConversationUpdate(conversationsData);
                    }
                  }, 0);
                }
              }
            }
          }
        } catch (error) {
          // Erro completamente silencioso no auto-refresh
          console.debug("[Auto-refresh] Erro silencioso:", error);
        }

        // Agendar próximo refresh se ainda estiver montado
        if (isMountedRef.current) {
          scheduleRefresh();
        }
      }
    }, refreshInterval);
  }, [
    account?.cpf,
    refreshInterval,
    generateConversationsHash,
    onConversationUpdate,
  ]);

  // Efeito para configurar listeners do socket
  useEffect(() => {
    const socketInstance = socket?.current;
    const isConnected = socketInstance?.connected;

    if (isConnected) {
      const handleNewMessage = (data: MessageType) => {
        if (!isMountedRef.current) return;

        try {
          const messagePhone = data.from || data.to;
          if (messagePhone) {
            updateConversation(messagePhone, data);
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Erro ao processar nova mensagem";
          showError(errorMessage);
        }
      };

      const handleNewMessageBatch = (
        data: MessageType[] | Record<string, MessageType>,
      ) => {
        if (!isMountedRef.current) return;

        try {
          // Converter para array se for objeto com índices numéricos
          const messagesArray = Array.isArray(data)
            ? data
            : Object.values(data);

          messagesArray.forEach((message) => {
            const messagePhone = message.from || message.to;
            if (messagePhone) {
              updateConversation(messagePhone, message);
            }
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Erro ao processar batch de mensagens";
          showError(errorMessage);
        }
      };

      const handleMessageStatusUpdate = (data: {
        messageId: string;
        status: string;
        friendlyErrorMessage?: string;
      }) => {
        if (!isMountedRef.current) return;

        if (data.friendlyErrorMessage) {
          showError(data.friendlyErrorMessage);
        }

        try {
          // Atualizar status da mensagem se necessário
          setConversations((prev) => {
            return prev.map((conv) => {
              const lastMsg = conv.last_message;
              if (
                lastMsg &&
                (lastMsg._id === data.messageId ||
                  (lastMsg as any).rawWebhookData?.id === data.messageId)
              ) {
                return {
                  ...conv,
                  last_message: {
                    ...lastMsg,
                    status: data.status as MessageStatus,
                  } as MessageType,
                };
              }
              return conv;
            });
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Erro ao atualizar status da mensagem";
          showError(errorMessage);
        }
      };

      const handleAllMessagesRead = (data: {
        phoneNumber: string;
        readBy: { id: string; name: string };
        readAt: Date;
        count: number;
        companyId: string;
      }) => {
        if (!isMountedRef.current) return;

        try {
          // Atualizar status de leitura das mensagens
          setConversations((prev) => {
            return prev.map((conv) => {
              const convPhone = conv.valid_telephone || conv.telephone;
              if (
                normalizePhoneNumber(convPhone) ===
                  normalizePhoneNumber(data.phoneNumber) &&
                conv.last_message
              ) {
                return {
                  ...conv,
                  last_message: {
                    ...conv.last_message,
                    readStatus: {
                      isRead: true,
                      readBy: data.readBy,
                      readAt: data.readAt,
                    },
                  },
                };
              }
              return conv;
            });
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Erro ao atualizar status de leitura";
          showError(errorMessage);
        }
      };

      // Adicionar listeners
      socketInstance.on("newMessage", handleNewMessage);
      socketInstance.on("newMessageBatch", handleNewMessageBatch);
      socketInstance.on("messageStatusUpdate", handleMessageStatusUpdate);
      socketInstance.on("allMessagesRead", handleAllMessagesRead);

      // Cleanup function
      return () => {
        if (socketInstance && isMountedRef.current) {
          socketInstance.off("newMessage", handleNewMessage);
          socketInstance.off("newMessageBatch", handleNewMessageBatch);
          socketInstance.off("messageStatusUpdate", handleMessageStatusUpdate);
          socketInstance.off("allMessagesRead", handleAllMessagesRead);
        }
      };
    }
  }, [socket, updateConversation, showError]);

  // Cleanup ao desmontar
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      // Limpar timeouts
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }
    };
  }, []);

  // Auto-refresh se habilitado
  useEffect(() => {
    if (!autoRefresh || !account?.cpf) return;

    const initialDelay = setTimeout(() => {
      scheduleRefresh();
    }, 60000); // 60 segundos iniciais

    return () => {
      clearTimeout(initialDelay);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [autoRefresh, account?.cpf, scheduleRefresh]);

  // Carregar conversas inicialmente
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      // Limpar todos os timeouts
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }

      // Limpar queue de updates para evitar vazamentos
      updateQueueRef.current = [];

      // Resetar flags de processamento
      processingRef.current = false;

      // Limpar cache de updates
      lastUpdateRef.current.clear();
    };
  }, []);

  // Função para forçar refresh
  const refresh = useCallback(() => {
    return loadConversations(true);
  }, [loadConversations]);

  // Função para obter estatísticas
  const getStats = useCallback(() => {
    return {
      totalConversations: conversations.length,
      queuedUpdates: updateQueueRef.current.length,
      isProcessing: processingRef.current,
      lastUpdates: lastUpdateRef.current.size,
    };
  }, [conversations.length]);

  // Cleanup adequado para evitar vazamentos de memória
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      // Limpar timeouts
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      if (batchTimeoutRef.current) {
        clearTimeout(batchTimeoutRef.current);
        batchTimeoutRef.current = null;
      }

      // Limpar refs para liberar memória
      conversationsRef.current = [];
      lastUpdateRef.current.clear();
      updateQueueRef.current = [];
      processingRef.current = false;
      lastConversationsHashRef.current = "";
      loadingRef.current = false;
    };
  }, []);

  return {
    conversations,
    loading,
    error,
    refresh,
    getStats,
    updateConversation,
  };
};

// Hook para gerenciar conversas por campanha com paginação
export const useCampaignConversations = ({
  campaignId,
  onConversationUpdate,
}: any) => {
  const [conversations, setConversations] = useState<ConversationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const { account } = useAccount();
  const { showError } = useToast();

  const limit = 50; // 50 contatos por página

  // Função para carregar conversas da campanha
  const loadCampaignConversations = useCallback(
    async (pageNum = 1, append = false) => {
      if (!account?.email || !campaignId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await apiService.get<ContactResponseWithHasMore>(
          "/contacts/campaign",
          {
            params: {
              email: account.email,
              campaignId: campaignId,
              page: pageNum,
              limit: limit,
            },
          },
        );

        if (response.status === "success") {
          const responseData = response.data as ContactResponseWithHasMore;

          if (Array.isArray(responseData.contacts)) {
            const conversationsData = responseData.contacts.map(
              (contact): ConversationData => ({
                id: contact.id.toString(),
                name: contact.name,
                email: contact.email,
                telephone: contact.telephone || contact.phone || "",
                valid_name: contact.valid_name || contact.name,
                valid_telephone:
                  contact.valid_telephone ||
                  contact.telephone ||
                  contact.phone ||
                  "",
                status: contact.status || "ATIVO",
                campaign_name: contact.campaign_name,
                campaign_id: contact.campaign_id.toString(),
                attendant_id: contact.attendant_id
                  ? contact.attendant_id.toString()
                  : "",
                attendant_name: contact.attendant_name,
                coord_id: contact.coord_id ? contact.coord_id.toString() : "",
                coordinator_name: contact.coordinator_name,
                company_id: contact.company_id.toString(),
                last_message: contact.last_message,
                is_online: false,
                ai_state: contact.ai_state
                  ? {
                      isAIActive: contact.ai_state.isAIActive,
                      disabledReason: contact.ai_state.disabledReason,
                      updatedAt: new Date(contact.ai_state.updatedAt),
                    }
                  : {
                      isAIActive: true,
                      disabledReason: undefined,
                      updatedAt: new Date(),
                    },
              }),
            );

            if (append) {
              // Remover duplicatas baseado apenas no ID da conversa (que é único)
              setConversations((prev) => {
                const existingIds = new Set(prev.map((conv) => conv.id));
                const newConversations = conversationsData.filter((conv) => {
                  return !existingIds.has(conv.id);
                });

                return [...prev, ...newConversations];
              });
            } else {
              setConversations(conversationsData);
            }

            setHasMore(responseData.hasMore || false);
            setPage(pageNum);

            if (onConversationUpdate) {
              onConversationUpdate(conversationsData);
            }
          } else {
            throw new Error("Formato de resposta inválido");
          }
        } else {
          throw new Error("Erro na resposta da API");
        }
      } catch (error) {
        console.error("Erro ao carregar conversas da campanha:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Erro desconhecido";
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [account?.email, campaignId, showError, onConversationUpdate],
  );

  // Função para carregar mais conversas (pagination)
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadCampaignConversations(page + 1, true);
    }
  }, [loading, hasMore, page, loadCampaignConversations]);

  // Carregar conversas inicialmente
  useEffect(() => {
    setPage(1);
    setConversations([]);
    setHasMore(true);
    loadCampaignConversations(1, false);
  }, [campaignId, loadCampaignConversations]);

  return {
    conversations,
    loading,
    error,
    hasMore,
    loadMore,
    refresh: () => loadCampaignConversations(1, false),
  };
};

// Hook para gerenciar múltiplas campanhas simultaneamente
export const useMultipleCampaigns = ({
  campaigns,
  onConversationUpdate,
  onNewMessage,
  eagerLoad = true,
}: UseMultipleCampaignsProps) => {
  const [campaignConversations, setCampaignConversations] = useState<
    Map<string, ConversationData[]>
  >(new Map());
  const [campaignLoadingStates, setCampaignLoadingStates] = useState<
    Map<string, boolean>
  >(new Map());
  const [campaignHasMoreStates, setCampaignHasMoreStates] = useState<
    Map<string, boolean>
  >(new Map());
  const [campaignPages, setCampaignPages] = useState<Map<string, number>>(
    new Map(),
  );
  const [campaignTotals, setCampaignTotals] = useState<Map<string, number>>(
    new Map(),
  );
  const [campaignInitialized, setCampaignInitialized] = useState<
    Map<string, boolean>
  >(new Map());
  const { account, socket } = useAccount();
  const { showError } = useToast();

  // Refs para armazenar callbacks e evitar recriações
  const onConversationUpdateRef = useRef(onConversationUpdate);
  const onNewMessageRef = useRef(onNewMessage);

  // Atualizar refs quando os callbacks mudam
  useEffect(() => {
    onConversationUpdateRef.current = onConversationUpdate;
  }, [onConversationUpdate]);

  useEffect(() => {
    onNewMessageRef.current = onNewMessage;
  }, [onNewMessage]);

  // Função para normalizar números de telefone (mesma do backend)
  const normalizePhoneNumber = useCallback((phone: string): string => {
    if (!phone) return "";
    // Usar a mesma normalização do backend: remove caracteres não numéricos e remove 55 do início
    return phone.replace(/[^0-9]/g, "").replace(/^55/, "");
  }, []);

  const limit = 50; // 50 contatos por página

  const loadCampaignConversations = useCallback(
    async (
      campaignId: string | number,
      campaignName: string,
      pageNum = 1,
      append = false,
    ) => {
      if (!account?.email) {
        return;
      }

      setCampaignLoadingStates((prev) => new Map(prev).set(campaignName, true));

      try {
        const response = await apiService.get<ContactResponseWithHasMore>(
          "/contacts/campaign",
          {
            params: {
              email: account.email,
              campaignId: campaignId,
              page: pageNum,
              limit: limit,
            },
          },
        );

        if (response.status === "success") {
          const responseData = response.data as ContactResponseWithHasMore;

          if (Array.isArray(responseData.contacts)) {
            const conversationsData = responseData.contacts.map(
              (contact): ConversationData => ({
                id: contact.id.toString(),
                name: contact.name,
                email: contact.email,
                telephone: contact.telephone || contact.phone || "",
                valid_name: contact.valid_name || contact.name,
                valid_telephone:
                  contact.valid_telephone ||
                  contact.telephone ||
                  contact.phone ||
                  "",
                status: contact.status || "ATIVO",
                campaign_name: contact.campaign_name,
                campaign_id: contact.campaign_id.toString(),
                attendant_id: contact.attendant_id
                  ? contact.attendant_id.toString()
                  : "",
                attendant_name: contact.attendant_name,
                coord_id: contact.coord_id ? contact.coord_id.toString() : "",
                coordinator_name: contact.coordinator_name,
                company_id: contact.company_id.toString(),
                last_message: contact.last_message,
                is_online: false,
                ai_state: contact.ai_state
                  ? {
                      isAIActive: contact.ai_state.isAIActive,
                      disabledReason: contact.ai_state.disabledReason,
                      updatedAt: new Date(contact.ai_state.updatedAt),
                    }
                  : {
                      isAIActive: true,
                      disabledReason: undefined,
                      updatedAt: new Date(),
                    },
              }),
            );

            setCampaignConversations((prev) => {
              const newMap = new Map(prev);
              if (append) {
                const existing = newMap.get(campaignName) || [];

                // Remover duplicatas baseado apenas no ID da conversa (que é único)
                const existingIds = new Set(existing.map((conv) => conv.id));
                const newConversations = conversationsData.filter((conv) => {
                  return !existingIds.has(conv.id);
                });

                const finalConversations = [...existing, ...newConversations];

                newMap.set(campaignName, finalConversations);
              } else {
                newMap.set(campaignName, conversationsData);
              }
              return newMap;
            });

            setCampaignHasMoreStates((prev) =>
              new Map(prev).set(campaignName, responseData.hasMore || false),
            );
            setCampaignPages((prev) =>
              new Map(prev).set(campaignName, pageNum),
            );
            setCampaignTotals((prev) =>
              new Map(prev).set(campaignName, responseData.total || 0),
            );

            if (onConversationUpdateRef.current && !append) {
              onConversationUpdateRef.current(conversationsData);
            }
          } else {
            throw new Error("Formato de resposta inválido");
          }
        } else {
          throw new Error("Erro na resposta da API");
        }
      } catch (error) {
        console.error("Erro ao carregar conversas da campanha:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Erro desconhecido";
        showError(errorMessage);
      } finally {
        setCampaignLoadingStates((prev) =>
          new Map(prev).set(campaignName, false),
        );
      }
    },
    [account?.email, showError],
  );

  // Função para carregar mais conversas de uma campanha
  const loadMoreForCampaign = useCallback(
    (campaignName: string) => {
      const campaign = campaigns.find((c) => c.name === campaignName);
      if (!campaign) return;

      const currentPage = campaignPages.get(campaignName) || 0;
      const loading = campaignLoadingStates.get(campaignName) || false;
      const hasMore = campaignHasMoreStates.get(campaignName) || false;

      // Verificar se já não está carregando e se há mais páginas
      if (!loading && hasMore) {
        loadCampaignConversations(
          campaign.id,
          campaignName,
          currentPage + 1,
          true,
        );
      }
    },
    [campaigns, campaignPages, campaignLoadingStates, campaignHasMoreStates],
  );

  // Socket listeners para atualizações em tempo real
  useEffect(() => {
    if (!socket?.current) {
      return;
    }

    const socketInstance = socket?.current;

    const handleNewMessage = (data: MessageType) => {
      // O backend envia a mensagem diretamente, não em um objeto com phoneNumber
      const message = data;
      if (!message) return;

      // Extrair o número de telefone da mensagem
      const phoneNumber = message.from || message.to;
      if (!phoneNumber) return;

      // Normalizar o número de telefone
      const normalizedPhone = normalizePhoneNumber(phoneNumber);

      setCampaignConversations((prevConversations) => {
        const updatedConversations = new Map(prevConversations);
        let hasUpdates = false;

        updatedConversations.forEach((conversations, campaignName) => {
          const updatedConversationsList = conversations.map((conv) => {
            const convPhone = conv.valid_telephone || conv.telephone;
            const normalizedConvPhone = normalizePhoneNumber(convPhone);

            if (normalizedConvPhone === normalizedPhone) {
              hasUpdates = true;
              return {
                ...conv,
                last_message: {
                  _id: message._id,
                  type: message.type,
                  content: message.content,
                  timestamp: message.timestamp,
                  sentBy: message.sentBy,
                  from: message.from,
                  to: message.to,
                  status: message.status as MessageStatus,
                  readStatus: message.readStatus,
                },
              };
            }
            return conv;
          });

          updatedConversations.set(campaignName, updatedConversationsList);
        });

        if (hasUpdates) {
          // Callback único sem timeout
          if (onConversationUpdateRef.current) {
            onConversationUpdateRef.current(
              Array.from(updatedConversations.values()).flat(),
            );
          }
        }

        return updatedConversations;
      });

      // Callback onNewMessage para todas as mensagens reais
      if (onNewMessageRef.current && message._id && message.content) {
        onNewMessageRef.current(phoneNumber, message);
      }
    };

    const handleNewMessageBatch = (
      data: MessageType[] | Record<string, MessageType>,
    ) => {
      // Converter para array se for objeto com índices numéricos
      const messagesArray = Array.isArray(data) ? data : Object.values(data);

      messagesArray.forEach((message) => {
        if (!message) return;

        // Extrair o número de telefone da mensagem
        const phoneNumber = message.from || message.to;
        if (!phoneNumber) return;

        // Normalizar o número de telefone
        const normalizedPhone = normalizePhoneNumber(phoneNumber);
        console.log(
          `🐛 [DEBUG FRONTEND] handleNewMessageBatch processando mensagem de: ${phoneNumber} -> normalizado: ${normalizedPhone}`,
        );

        setCampaignConversations((prevConversations) => {
          const updatedConversations = new Map(prevConversations);
          let hasUpdates = false;

          updatedConversations.forEach((conversations, campaignName) => {
            const updatedConversationsList = conversations.map((conv) => {
              const convPhone = conv.valid_telephone || conv.telephone;
              const normalizedConvPhone = normalizePhoneNumber(convPhone);

              if (normalizedConvPhone === normalizedPhone) {
                hasUpdates = true;
                return {
                  ...conv,
                  last_message: {
                    _id: message._id,
                    type: message.type,
                    content: message.content,
                    timestamp: message.timestamp,
                    sentBy: message.sentBy,
                    from: message.from,
                    to: message.to,
                    status: message.status,
                    readStatus: message.readStatus,
                  },
                };
              }
              return conv;
            });

            updatedConversations.set(campaignName, updatedConversationsList);
          });

          if (hasUpdates) {
            // Callback único sem timeout
            if (onConversationUpdateRef.current) {
              onConversationUpdateRef.current(
                Array.from(updatedConversations.values()).flat(),
              );
            }
          }

          return updatedConversations;
        });

        // Callback onNewMessage para todas as mensagens reais
        if (onNewMessageRef.current && message._id && message.content) {
          onNewMessageRef.current(phoneNumber, message);
        }
      });
    };

    // Adicionar listeners
    socketInstance.on("newMessage", handleNewMessage);
    socketInstance.on("newMessageBatch", handleNewMessageBatch);

    return () => {
      socketInstance.off("newMessage", handleNewMessage);
      socketInstance.off("newMessageBatch", handleNewMessageBatch);
    };
  }, [socket, normalizePhoneNumber]);

  // Carregar conversas inicialmente para todas as campanhas (apenas quando eagerLoad=true)
  useEffect(() => {
    if (campaigns.length === 0) return;

    // Limpar estados anteriores quando as campanhas mudam
    setCampaignConversations(new Map());
    setCampaignLoadingStates(new Map());
    setCampaignHasMoreStates(new Map());
    setCampaignPages(new Map());
    setCampaignTotals(new Map());
    setCampaignInitialized(new Map());

    if (eagerLoad) {
      campaigns.forEach((campaign) => {
        loadCampaignConversations(campaign.id, campaign.name, 1, false);
        setCampaignInitialized((prev) =>
          new Map(prev).set(campaign.name, true),
        );
      });
    }
  }, [campaigns, eagerLoad]);

  // Função para garantir que uma campanha esteja carregada (lazy init)
  const ensureCampaignLoaded = useCallback(
    (campaignName: string) => {
      const initialized = campaignInitialized.get(campaignName) || false;
      if (initialized) return;

      const campaign = campaigns.find((c) => c.name === campaignName);
      if (!campaign) return;

      setCampaignInitialized((prev) => new Map(prev).set(campaignName, true));
      loadCampaignConversations(campaign.id, campaignName, 1, false);
    },
    [campaignInitialized, campaigns],
  );

  return {
    campaignConversations,
    campaignLoadingStates,
    campaignHasMoreStates,
    campaignTotals,
    loadMoreForCampaign,
    ensureCampaignLoaded,
    refresh: useCallback(() => {
      campaigns.forEach((campaign) => {
        loadCampaignConversations(campaign.id, campaign.name, 1, false);
      });
    }, [campaigns]),
  };
};

// Hook para gerenciar campanhas com paginação
export const useCampaignsWithPagination = () => {
  const [campaigns, setCampaigns] = useState<
    { id: string | number; name: string }[]
  >([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const { showError } = useToast();

  const limit = 20; // 20 campanhas por página

  // Função para carregar campanhas
  const loadCampaigns = useCallback(
    async (pageNum = 1, append = false) => {
      setLoading(true);

      try {
        const response = await apiService.get<{
          status: "success";
          data: {
            campaigns: Array<{ id: string | number; name: string }>;
            total: number;
            page: number;
            limit: number;
            hasMore: boolean;
            totalPages: number;
          };
        }>("/contacts/campaigns", {
          params: { page: pageNum, limit },
        });

        if (response.status === "success" && response.data) {
          const {
            campaigns: newCampaigns,
            total: totalCampaigns,
            hasMore: moreAvailable,
            totalPages: pages,
          } = response.data.data;

          if (append) {
            setCampaigns((prev) => [...prev, ...newCampaigns]);
          } else {
            setCampaigns(newCampaigns);
          }

          setTotal(totalCampaigns);
          setHasMore(moreAvailable);
          setTotalPages(pages);
          setPage(pageNum);
        }
      } catch (error) {
        console.error("Erro ao carregar campanhas:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Erro ao carregar campanhas";
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [showError],
  );

  // Função para carregar mais campanhas
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadCampaigns(page + 1, true);
    }
  }, [loading, hasMore, page, loadCampaigns]);

  // Carregar campanhas inicialmente
  useEffect(() => {
    setPage(1);
    setCampaigns([]);
    setHasMore(true);
    loadCampaigns(1, false);
  }, [loadCampaigns]);

  return {
    campaigns,
    loading,
    hasMore,
    total,
    totalPages,
    currentPage: page,
    loadMore,
    refresh: () => loadCampaigns(1, false),
  };
};
